const DEFAULT_CONFIG = {
  enabled: false,
  endpoint: '/radicait-api/ohif-annotations',
  promptForIdentity: true,
  identityQueryParams: ['reviewer', 'reader', 'user', 'name'],
  localStorageKey: 'radicait.ohif.reviewerName',
  sessionStorageKey: 'radicait.ohif.annotationSessionId',
  flushDelayMs: 750,
};

export function setupRadicaitAnnotationAutosave({ appConfig, servicesManager }) {
  const config = {
    ...DEFAULT_CONFIG,
    ...(appConfig?.radicaitAnnotationAutosave || {}),
  };

  if (!config.enabled || typeof window === 'undefined') {
    return;
  }

  if (window.__radicaitAnnotationAutosaveInstalled) {
    return;
  }
  window.__radicaitAnnotationAutosaveInstalled = true;

  const measurementService = servicesManager?.services?.measurementService;
  if (!measurementService?.subscribe) {
    console.warn('[Radicait annotations] MeasurementService unavailable; autosave disabled.');
    return;
  }

  const state = {
    config,
    measurementCache: new Map(),
    pendingEvents: new Map(),
    flushTimer: null,
    reviewerIdentity: readReviewerIdentity(config),
    viewerSessionId: getOrCreateSessionId(config),
  };

  if (!state.reviewerIdentity?.name && config.promptForIdentity !== false) {
    showReviewerIdentityDialog(config, identity => {
      state.reviewerIdentity = identity;
    });
  }

  const subscribe = (eventName, callback) => measurementService.subscribe(eventName, callback);
  const subscriptions = [
    subscribe(measurementService.EVENTS.MEASUREMENT_ADDED, detail =>
      handleMeasurementUpsert(state, 'measurement_added', detail?.measurement)
    ),
    subscribe(measurementService.EVENTS.RAW_MEASUREMENT_ADDED, detail =>
      handleMeasurementUpsert(state, 'raw_measurement_added', detail?.measurement)
    ),
    subscribe(measurementService.EVENTS.MEASUREMENT_UPDATED, detail =>
      handleMeasurementUpsert(state, 'measurement_updated', detail?.measurement)
    ),
    subscribe(measurementService.EVENTS.MEASUREMENT_REMOVED, detail =>
      handleMeasurementRemoved(state, detail?.measurement)
    ),
    subscribe(measurementService.EVENTS.MEASUREMENTS_CLEARED, detail =>
      handleMeasurementsCleared(state, detail?.measurements)
    ),
  ];

  window.addEventListener('pagehide', () => flushPendingEvents(state, true));
  window.__radicaitAnnotationAutosave = {
    flush: () => flushPendingEvents(state, false),
    setReviewerName: name => {
      const reviewerName = String(name || '').trim();
      if (!reviewerName) {
        return;
      }
      storeReviewerName(config, reviewerName);
      state.reviewerIdentity = { name: reviewerName, source: 'manual' };
    },
    getReviewerName: () => state.reviewerIdentity?.name || null,
    unsubscribe: () => subscriptions.forEach(subscription => subscription?.unsubscribe?.()),
  };
}

function handleMeasurementUpsert(state, eventType, measurement) {
  if (!measurement?.uid) {
    return;
  }

  state.measurementCache.set(measurement.uid, measurement);
  queueEvent(state, buildAnnotationEvent(state, eventType, measurement, false));
}

function handleMeasurementRemoved(state, measurementUID) {
  if (!measurementUID) {
    return;
  }

  const measurement = state.measurementCache.get(measurementUID) || {
    uid: measurementUID,
  };
  state.measurementCache.delete(measurementUID);
  queueEvent(state, buildAnnotationEvent(state, 'measurement_removed', measurement, true), true);
}

function handleMeasurementsCleared(state, measurements) {
  const clearedMeasurements = Array.isArray(measurements)
    ? measurements
    : Object.values(measurements || {});

  clearedMeasurements.forEach(measurement => {
    if (!measurement?.uid) {
      return;
    }
    state.measurementCache.delete(measurement.uid);
    queueEvent(state, buildAnnotationEvent(state, 'measurement_cleared', measurement, true), true);
  });
}

function buildAnnotationEvent(state, eventType, measurement, deleted) {
  const identity = getReviewerIdentity(state);
  if (!identity?.name) {
    return null;
  }

  const cleanMeasurement = sanitizeForJson(measurement) || {};
  const params = new URLSearchParams(window.location.search);
  const measurementUID = cleanMeasurement.uid || measurement.uid;

  return {
    payloadVersion: 'radicait-ohif-annotation-v1',
    eventId: `${Date.now()}-${randomId()}`,
    eventType,
    deleted,
    clientCreatedAt: new Date().toISOString(),
    retryCount: 0,
    reviewerName: identity.name,
    reviewerSlug: slugify(identity.name),
    identitySource: identity.source,
    identityParam: identity.param || null,
    viewerSessionId: state.viewerSessionId,
    datasource: params.get('datasources') || params.get('dataSources') || null,
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    userAgent: window.navigator.userAgent,
    measurementUID,
    toolName: cleanMeasurement.toolName || null,
    label: cleanMeasurement.label || null,
    referenceStudyUID: cleanMeasurement.referenceStudyUID || null,
    referenceSeriesUID: cleanMeasurement.referenceSeriesUID || null,
    SOPInstanceUID: cleanMeasurement.SOPInstanceUID || null,
    FrameOfReferenceUID: cleanMeasurement.FrameOfReferenceUID || null,
    displaySetInstanceUID: cleanMeasurement.displaySetInstanceUID || null,
    referencedImageId: cleanMeasurement.referencedImageId || null,
    measurement: cleanMeasurement,
  };
}

function queueEvent(state, event, immediate = false) {
  if (!event?.measurementUID) {
    return;
  }

  const queueKey = event.deleted
    ? `delete:${event.measurementUID}:${event.eventId}`
    : `latest:${event.measurementUID}`;
  state.pendingEvents.set(queueKey, event);

  if (immediate) {
    flushPendingEvents(state, false);
    return;
  }

  if (state.flushTimer) {
    window.clearTimeout(state.flushTimer);
  }
  state.flushTimer = window.setTimeout(
    () => flushPendingEvents(state, false),
    state.config.flushDelayMs
  );
}

async function flushPendingEvents(state, useBeacon) {
  if (state.flushTimer) {
    window.clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }

  const events = Array.from(state.pendingEvents.values());
  if (!events.length) {
    return;
  }
  state.pendingEvents.clear();

  const body = JSON.stringify({ events });
  if (useBeacon && window.navigator?.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    window.navigator.sendBeacon(state.config.endpoint, blob);
    return;
  }

  try {
    const response = await window.fetch(state.config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: events.length <= 8,
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.warn('[Radicait annotations] autosave failed:', error);
    events.forEach(event => {
      const retryCount = Number(event.retryCount || 0) + 1;
      if (retryCount > 3) {
        return;
      }
      state.pendingEvents.set(`retry:${event.measurementUID}:${event.eventId}`, {
        ...event,
        retryCount,
      });
    });
    if (state.pendingEvents.size && !state.flushTimer) {
      state.flushTimer = window.setTimeout(
        () => flushPendingEvents(state, false),
        state.config.flushDelayMs * 2
      );
    }
  }
}

function getReviewerIdentity(state) {
  if (state.reviewerIdentity?.name) {
    return state.reviewerIdentity;
  }
  state.reviewerIdentity = readReviewerIdentity(state.config);
  if (!state.reviewerIdentity?.name && state.config.promptForIdentity !== false) {
    showReviewerIdentityDialog(state.config, identity => {
      state.reviewerIdentity = identity;
    });
    return null;
  }
  if (!state.reviewerIdentity?.name) {
    return { name: 'anonymous', source: 'anonymous' };
  }
  return state.reviewerIdentity;
}

function readReviewerIdentity(config) {
  const params = new URLSearchParams(window.location.search);
  for (const paramName of config.identityQueryParams) {
    const paramValue = params.get(paramName);
    if (paramValue?.trim()) {
      const name = paramValue.trim();
      storeReviewerName(config, name);
      return { name, source: 'url', param: paramName };
    }
  }

  const storedName = readStoredReviewerName(config);
  if (storedName) {
    return { name: storedName, source: 'localStorage' };
  }

  return null;
}

function readStoredReviewerName(config) {
  try {
    return window.localStorage.getItem(config.localStorageKey);
  } catch (_error) {
    return '';
  }
}

function storeReviewerName(config, name) {
  try {
    window.localStorage.setItem(config.localStorageKey, name);
  } catch (_error) {
    // Ignore storage failures in locked-down browser contexts.
  }
}

function showReviewerIdentityDialog(config, onSubmit) {
  if (typeof document === 'undefined') {
    return;
  }

  const existingDialog = document.querySelector('[data-radicait-annotator-dialog]');
  if (existingDialog) {
    return;
  }

  const mountDialog = () => {
    if (document.querySelector('[data-radicait-annotator-dialog]')) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.setAttribute('data-radicait-annotator-dialog', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'radicait-annotator-title');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:2147483647',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'padding:24px',
      'background:rgba(6,12,24,0.78)',
      'backdrop-filter:blur(3px)',
    ].join(';');

    const panel = document.createElement('form');
    panel.style.cssText = [
      'width:min(380px,calc(100vw - 48px))',
      'box-sizing:border-box',
      'border:1px solid rgba(148,163,184,0.35)',
      'border-radius:8px',
      'background:#101827',
      'box-shadow:0 20px 70px rgba(0,0,0,0.45)',
      'padding:22px',
      'font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      'color:#f8fafc',
    ].join(';');

    const title = document.createElement('h2');
    title.id = 'radicait-annotator-title';
    title.textContent = 'Reader Name';
    title.style.cssText = [
      'margin:0 0 14px',
      'font-size:18px',
      'font-weight:650',
      'line-height:1.25',
      'letter-spacing:0',
    ].join(';');

    const label = document.createElement('label');
    label.textContent = 'Name';
    label.setAttribute('for', 'radicait-annotator-name-input');
    label.style.cssText = [
      'display:block',
      'margin:0 0 7px',
      'font-size:13px',
      'font-weight:600',
      'letter-spacing:0',
      'color:#cbd5e1',
    ].join(';');

    const input = document.createElement('input');
    input.id = 'radicait-annotator-name-input';
    input.type = 'text';
    input.autocomplete = 'name';
    input.required = true;
    input.maxLength = 120;
    input.style.cssText = [
      'width:100%',
      'box-sizing:border-box',
      'border:1px solid rgba(148,163,184,0.45)',
      'border-radius:6px',
      'background:#0b1220',
      'color:#f8fafc',
      'font-size:15px',
      'line-height:1.35',
      'letter-spacing:0',
      'padding:10px 11px',
      'outline:none',
    ].join(';');

    const error = document.createElement('div');
    error.textContent = 'Enter a name to continue.';
    error.style.cssText = [
      'display:none',
      'margin:8px 0 0',
      'font-size:12px',
      'line-height:1.35',
      'letter-spacing:0',
      'color:#fecaca',
    ].join(';');

    const button = document.createElement('button');
    button.type = 'submit';
    button.textContent = 'Continue';
    button.style.cssText = [
      'width:100%',
      'margin:18px 0 0',
      'border:0',
      'border-radius:6px',
      'background:#38bdf8',
      'color:#082f49',
      'font-size:14px',
      'font-weight:700',
      'line-height:1.2',
      'letter-spacing:0',
      'padding:10px 14px',
      'cursor:pointer',
    ].join(';');

    panel.addEventListener('submit', event => {
      event.preventDefault();
      const name = input.value.trim();
      if (!name) {
        error.style.display = 'block';
        input.focus();
        return;
      }

      storeReviewerName(config, name);
      overlay.remove();
      onSubmit({ name, source: 'startupDialog' });
    });

    input.addEventListener('input', () => {
      error.style.display = 'none';
    });

    panel.append(title, label, input, error, button);
    overlay.append(panel);
    document.body.append(overlay);
    window.setTimeout(() => input.focus(), 0);
  };

  if (document.body) {
    mountDialog();
    return;
  }

  window.addEventListener('DOMContentLoaded', mountDialog, { once: true });
}

function getOrCreateSessionId(config) {
  try {
    const existing = window.sessionStorage.getItem(config.sessionStorageKey);
    if (existing) {
      return existing;
    }
    const sessionId = randomId();
    window.sessionStorage.setItem(config.sessionStorageKey, sessionId);
    return sessionId;
  } catch (_error) {
    return randomId();
  }
}

function sanitizeForJson(value, seen = new WeakSet(), depth = 0) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') {
    return undefined;
  }
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (depth > 12) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const items = value
      .map(item => sanitizeForJson(item, seen, depth + 1))
      .filter(item => item !== undefined);
    return items.map(item => (Array.isArray(item) ? { values: item } : item));
  }
  if (typeof value === 'object') {
    if (seen.has(value)) {
      return undefined;
    }
    seen.add(value);
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      const sanitized = sanitizeForJson(item, seen, depth + 1);
      if (sanitized !== undefined) {
        output[key] = sanitized;
      }
    });
    seen.delete(value);
    return output;
  }
  return String(value);
}

function slugify(value) {
  return (
    String(value || 'anonymous')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'anonymous'
  );
}

function randomId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
